import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Archive, ArchiveRestore, Eye, Clock, X } from "lucide-react";

const STATION_TYPES = [
  { v: "umbrella", label: "Ombrelloni" }, { v: "table", label: "Tavoli" },
  { v: "sunbed", label: "Lettini" }, { v: "vip", label: "VIP" }, { v: "other", label: "Altro" },
];
const WEEKDAYS = [
  { v: 0, label: "Lun" }, { v: 1, label: "Mar" }, { v: 2, label: "Mer" },
  { v: 3, label: "Gio" }, { v: 4, label: "Ven" }, { v: 5, label: "Sab" }, { v: 6, label: "Dom" },
];

const emptyMenu = () => ({
  name: "", description: "", active: true, start_date: null, end_date: null,
  time_slots: [], days: null, station_types: [], category_ids: [], sort_order: 0,
});

export default function AdminMenus() {
  const [menus, setMenus] = useState([]);
  const [cats, setCats] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [m, c] = await Promise.all([
      api.get(`/admin/menus${includeArchived ? "?include_archived=true" : ""}`),
      api.get("/admin/categories"),
    ]);
    setMenus(m.data); setCats(c.data);
  };
  useEffect(() => { load(); }, [includeArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return menus;
    return menus.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [menus, search]);

  const duplicate = async (m) => {
    try { await api.post(`/admin/menus/${m.id}/duplicate`); load(); toast.success(`Duplicato "${m.name}"`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (m) => {
    try { await api.post(`/admin/menus/${m.id}/${m.archived ? "unarchive" : "archive"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async () => {
    try {
      const { data } = await api.get("/admin/menu/preview");
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="eyebrow">Gestione</div>
      <div className="flex justify-between items-start gap-3 mt-1 flex-wrap">
        <h1 className="text-4xl">Menu multipli</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={openPreview}
                  data-testid="menu-preview-btn"><Eye className="w-4 h-4 mr-1" />Vedi come cliente</Button>
          <MenuDialog categories={cats} onSaved={load} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input placeholder="Cerca menu…" value={search} onChange={(e) => setSearch(e.target.value)}
               className="max-w-xs" data-testid="menu-search" />
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived}
                  data-testid="menu-include-archived" />
          Mostra archiviati
        </label>
        <div className="text-xs text-ink-500 ml-auto">{filtered.length} menu</div>
      </div>

      <div className="mt-4 grid gap-3">
        {filtered.map((m) => (
          <div key={m.id} data-testid={`menu-row-${m.id}`}
               className={`card-elevated p-4 ${m.archived ? "opacity-60" : ""}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-serif text-2xl">{m.name}</h3>
                  {m.active ? <span className="chip bg-green-50 text-green-800 border-green-200">Attivo</span>
                            : <span className="chip">Inattivo</span>}
                  {m.archived && <span className="chip bg-amber-50 text-amber-800 border-amber-200">Archiviato</span>}
                </div>
                {m.description && <p className="text-sm text-ink-500 mt-1">{m.description}</p>}
                <div className="text-xs text-ink-500 mt-2 flex flex-wrap gap-3">
                  {m.start_date && <span>Da {m.start_date}</span>}
                  {m.end_date && <span>a {m.end_date}</span>}
                  {m.station_types?.length ? <span>Postazioni: {m.station_types.map((t) => STATION_TYPES.find((x) => x.v === t)?.label || t).join(", ")}</span> : <span>Tutte le postazioni</span>}
                  <span>{m.category_ids?.length || 0} categorie</span>
                  {m.time_slots?.length ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.time_slots.length} fasce</span> : <span>Sempre disponibile</span>}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <div className="flex items-center gap-1">
                  <MenuDialog menu={m} categories={cats} onSaved={load} trigger={
                    <button data-testid={`edit-menu-${m.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Pencil className="w-4 h-4" /></button>
                  } />
                  <button onClick={() => duplicate(m)} data-testid={`dup-menu-${m.id}`}
                          className="p-1.5 rounded-full hover:bg-sand-200"><Copy className="w-4 h-4" /></button>
                  <button onClick={() => archive(m)} data-testid={`arch-menu-${m.id}`}
                          className="p-1.5 rounded-full hover:bg-sand-200">
                    {m.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="card-elevated p-8 text-center text-ink-500">Nessun menu trovato.</div>}
      </div>

      {preview && <PreviewSheet data={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------

function MenuDialog({ menu, categories, onSaved, trigger }) {
  const isEdit = !!menu;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyMenu());

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setF({
        name: menu.name || "", description: menu.description || "",
        active: menu.active !== false,
        start_date: menu.start_date || null, end_date: menu.end_date || null,
        time_slots: menu.time_slots || [], days: menu.days || null,
        station_types: menu.station_types || [], category_ids: menu.category_ids || [],
        sort_order: menu.sort_order || 0,
      });
    } else setF(emptyMenu());
  }, [open, isEdit, menu]);

  const toggleStationType = (v) => {
    const s = new Set(f.station_types);
    s.has(v) ? s.delete(v) : s.add(v);
    setF({ ...f, station_types: Array.from(s) });
  };
  const toggleCat = (id) => {
    const s = new Set(f.category_ids);
    s.has(id) ? s.delete(id) : s.add(id);
    setF({ ...f, category_ids: Array.from(s) });
  };
  const addSlot = () => setF({ ...f, time_slots: [...f.time_slots, { from_time: null, to_time: null, days: null }] });
  const removeSlot = (i) => setF({ ...f, time_slots: f.time_slots.filter((_, idx) => idx !== i) });
  const setSlot = (i, patch) => setF({ ...f, time_slots: f.time_slots.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  const toggleSlotDay = (i, d) => {
    const days = new Set((f.time_slots[i].days) || []);
    days.has(d) ? days.delete(d) : days.add(d);
    setSlot(i, { days: Array.from(days).sort((a, b) => a - b) });
  };

  const save = async () => {
    try {
      const payload = { ...f };
      if (isEdit) await api.patch(`/admin/menus/${menu.id}`, payload);
      else await api.post("/admin/menus", payload);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Menu aggiornato" : "Menu creato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button data-testid="new-menu-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuovo menu</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? `Modifica ${menu.name}` : "Nuovo menu"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="menu-name" /></div>
          <div><Label>Descrizione</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="menu-desc" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Ordine</Label><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Da (data)</Label><Input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value || null })} data-testid="menu-start" /></div>
            <div><Label>A (data)</Label><Input type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value || null })} data-testid="menu-end" /></div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="menu-active" />
            <span>{f.active ? "Attivo (visibile ai clienti quando le condizioni sono rispettate)" : "Inattivo (nascosto ai clienti)"}</span>
          </div>

          <div>
            <Label>Postazioni abilitate</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {STATION_TYPES.map((s) => {
                const on = f.station_types.includes(s.v);
                return (
                  <button key={s.v} type="button" onClick={() => toggleStationType(s.v)}
                          data-testid={`menu-st-${s.v}`}
                          className={`px-2.5 py-1 rounded-full text-xs border ${on ? "border-bronze-600 bg-bronze-600 text-white" : "border-sand-300 bg-white"}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-ink-500 mt-1">Nessuna selezionata = tutte le postazioni.</div>
          </div>

          <div>
            <Label>Categorie associate ({f.category_ids.length})</Label>
            <div className="mt-1 grid grid-cols-2 gap-1 max-h-40 overflow-y-auto rounded-xl border border-sand-300 p-2">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer"
                       data-testid={`menu-cat-${c.id}`}>
                  <input type="checkbox" checked={f.category_ids.includes(c.id)} onChange={() => toggleCat(c.id)} />
                  {c.name} <span className="text-xs text-ink-500">· {c.department}</span>
                </label>
              ))}
              {categories.length === 0 && <div className="text-xs text-ink-500">Nessuna categoria</div>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Fasce orarie ({f.time_slots.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSlot} data-testid="menu-add-slot">
                <Plus className="w-3 h-3 mr-1" />Aggiungi fascia
              </Button>
            </div>
            {f.time_slots.length === 0 && <div className="text-xs text-ink-500 mt-1">Nessuna fascia = sempre disponibile.</div>}
            <div className="mt-2 space-y-2">
              {f.time_slots.map((s, i) => (
                <div key={i} className="rounded-xl border border-sand-300 p-3 space-y-2 bg-sand-50/50" data-testid={`menu-slot-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Dalle</Label>
                        <Input type="time" value={s.from_time || ""} onChange={(e) => setSlot(i, { from_time: e.target.value || null })} data-testid={`menu-slot-from-${i}`} />
                      </div>
                      <div><Label className="text-xs">Alle</Label>
                        <Input type="time" value={s.to_time || ""} onChange={(e) => setSlot(i, { to_time: e.target.value || null })} data-testid={`menu-slot-to-${i}`} />
                      </div>
                    </div>
                    <button type="button" onClick={() => removeSlot(i)} data-testid={`menu-slot-del-${i}`}
                            className="p-1.5 text-red-700 hover:bg-red-50 rounded-full"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {WEEKDAYS.map((d) => {
                      const on = (s.days || []).includes(d.v);
                      return (
                        <button key={d.v} type="button" onClick={() => toggleSlotDay(i, d.v)}
                                data-testid={`menu-slot-${i}-day-${d.v}`}
                                className={`px-2 py-0.5 rounded-full text-xs border ${on ? "border-bronze-600 bg-bronze-600 text-white" : "border-sand-300 bg-white"}`}>
                          {d.label}
                        </button>
                      );
                    })}
                    <span className="text-[11px] text-ink-500 self-center ml-1">Nessuno = tutti i giorni</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={save} className="w-full rounded-full" data-testid="menu-save">
            {isEdit ? "Salva modifiche" : "Crea menu"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewSheet({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="menu-preview-sheet">
        <div className="flex justify-between items-center">
          <div><div className="eyebrow">Anteprima cliente</div><h2 className="font-serif text-2xl">Menu attuale</h2></div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-sand-100"><X /></button>
        </div>
        <p className="text-xs text-ink-500 mt-2">Menu attivi: {data.menus_used?.join(", ") || "(nessuno)"}</p>
        <div className="mt-4 space-y-4">
          {data.categories.map((c) => {
            const prods = data.products.filter((p) => p.category_id === c.id);
            return (
              <div key={c.id}>
                <h3 className="font-serif text-lg">{c.name} <span className="text-xs text-ink-500">· {c.department}</span></h3>
                <ul className="mt-1 text-sm space-y-0.5">
                  {prods.map((p) => <li key={p.id} className="flex justify-between"><span>{p.name}</span><span>{(p.price_cents / 100).toFixed(2)} €</span></li>)}
                  {prods.length === 0 && <li className="text-ink-500 text-xs">Nessun prodotto disponibile</li>}
                </ul>
              </div>
            );
          })}
          {data.categories.length === 0 && <div className="text-ink-500">Nessuna categoria visibile in questo momento.</div>}
        </div>
      </div>
    </div>
  );
}
